// story: клиент сюжетного режима «Книга заклинаний».
//
// Что, где и когда — решает сервер (server/internal/game/story*.go); здесь
// только показ: существа-видео на плоскостях (StoryRenderer + VideoBank),
// HUD этапов и шёпотов (StoryHud), звуки, свет книги и призраков.
//
// main.ts вызывает: onHello (режим комнаты), onSnapshot (сюжетный блок),
// onEvent (EvType.Story; true — событие наше), frame (кадр), appendLights
// (свет), reset (новая сессия). Проход отрисовки подключается к Renderer
// через renderer.storyPass, пока режим активен.
//
// Кровь при попадании по зомби и старухе — те же кубики-осколки, что у
// игроков в DM (renderer.spawnChunkDebris, BLOOD_DEBRIS_COLOR). Точку берём
// из MobHurt, направление — из события, которое ранило в той же пачке: луч
// (Shot), взмах (WeaponFire), взрыв (Explosion). Горение и огнемёт крови не
// дают. Остальные события onEvent только подсматривает (возвращает false).

import { VIDEO_PACKED_ALPHA } from "../engine/meshLoader";
import { track } from "../analytics";
import type { PointLight, Renderer, StoryPassContext } from "../engine/renderer";
import { loadSfx, playOneShot } from "../engine/sfx";
import { BLOOD_DEBRIS_COLOR, BODY_FLESH_DEBRIS_CUBE_SIZE } from "../engine/voxel";
import {
  EvType, ShooterKind, ShotSurf, StoryEv, StoryFlag, StoryKind, StoryStage, StoryState,
  type GameEvent, type HelloMsg, type Snapshot,
} from "../net/protocol";
import { WeaponId } from "../weapons/catalog";
import {
  CORPSE_HOLD_SEC, CORPSE_SEC, CORPSE_SINK, DISSOLVE_SEC, KIND_LOOK, STAGE_TEXT, STORY_SOUNDS, VIDEO_SLOTS,
  slotFor,
} from "./catalog";
import { StoryHud } from "./storyHud";
import { hasT, t } from "../i18n";
import { StoryRenderer, type StoryQuad } from "./storyRenderer";
import { VideoBank, type OneShot, type VideoSource } from "./videoSlots";

/** Текст сюжета с сервера (он шлёт по-русски) на языке игрока: есть
 *  в словаре — перевод; нет (сервер новее клиента) — запасной текст этапа
 *  по номеру из STAGE_TEXT. По-русски — как раньше: строка сервера. */
function storyText(s: string, fallback?: string): string {
  if (s && hasT(s)) return t(s);
  if (fallback) return t(fallback);
  return s;
}

export interface StoryHost {
  renderer: Renderer;
  gl: WebGL2RenderingContext;
  /** Позиция ступней локального игрока и рыскание взгляда. */
  player(): { x: number; y: number; z: number; yaw: number } | null;
  myDeaths(): number;
  soundVolume(): number;
  /** «Ещё раз» на экране победы: новый забег (RecreateSolo, mode=story). */
  onAgain(): void;
  /** «В меню» на экране победы. */
  onMenu(): void;
}

interface MobView {
  id: number;
  kind: number;
  state: number;
  variant: number;
  x: number; y: number; z: number; yaw: number;
  tx: number; ty: number; tz: number; tyaw: number;
  hp: number;
  /** performance.now() входа в состояние (с поправкой на age из снапшота). */
  stateStart: number;
  oneShot: OneShot | null;
  mirror: boolean;
  flashUntil: number;
  /** Сила вспышки: шёпот картины — 1, попадание — едва заметно (кровь важнее). */
  flashAmp: number;
  seed: number;
  /** Сглаженная скорость по полу, м/с, и выбранная скорость ролика ходьбы. */
  spd: number;
  walkRate: number;
}

/** Чем ранили существо в текущей пачке событий (для направления крови). */
interface HitSource {
  /** Выстрел/взмах игрока: оружие и взгляд. */
  fire: { weapon: number; dx: number; dy: number; dz: number } | null;
  /** Луч, попавший в существо (ShotSurf.Npc): откуда и куда. */
  shot: { ox: number; oy: number; oz: number; hx: number; hy: number; hz: number } | null;
  /** Центр взрыва. */
  blast: { x: number; y: number; z: number } | null;
}

const TICK_MS = 1000 / 60;
const LERP_TAU = 0.09;
/** Сглаживание скорости существа для темпа шагов, с. */
const SPEED_TAU = 0.25;
/** Скорости проигрывания ходьбы: общий плеер на каждую (не на каждое существо). */
const WALK_RATES = [0.3, 0.6, 1, 1.5, 2, 2.5, 3, 3.5];
const HEAR_FULL = 4;
const HEAR_MAX = 38;
const TOTAL_STAGES = 4;
const END_DELAY_MS = 2300;
/** Вспышка при попадании: доля от вспышки шёпота (0,2 × 0,55 ≈ 11 % к красному). */
const HURT_FLASH_AMP = 0.2;
const FLASH_MS = 140;
/** Глубина существа ближе к камере на радиус тела: ступени под ним не режут ноги. */
const BODY_DEPTH_PULL = 0.35;
/** Кровь: капель на попадание / на смертельный удар (дробь — 9 попаданий). */
const BLOOD_DROPS_HIT = 18;
const BLOOD_DROPS_KILL = 32;
/** Три оттенка — капли не одним цветом. */
const BLOOD_SHADES: [number, number, number][] = [
  BLOOD_DEBRIS_COLOR,
  [BLOOD_DEBRIS_COLOR[0] * 0.72, BLOOD_DEBRIS_COLOR[1] * 0.6, BLOOD_DEBRIS_COLOR[2] * 0.6],
  [BLOOD_DEBRIS_COLOR[0] * 0.5, BLOOD_DEBRIS_COLOR[1] * 0.4, BLOOD_DEBRIS_COLOR[2] * 0.45],
];

export class StoryClient {
  private host: StoryHost;
  private active = false;
  private views = new Map<number, MobView>();
  private bank: VideoBank | null = null;
  private rend: StoryRenderer;
  private hud: StoryHud;
  private stage = 0;
  private flags = 0;
  private timeSec = 0;
  private kills = 0;
  private ghosts = 0;
  private wx = 0;
  private wy = 0;
  private wz = 0;
  private stageTitle = "";
  private bossHp = 100;
  private victoryAt = 0;
  private endShown = false;
  private endTimer = 0;
  private soundUrl = new Map<string, string>();
  private quads: StoryQuad[] = [];
  /** textureFor отдал настоящий ролик слота (не заглушку). */
  private lastRealClip = false;
  /** textureFor отдал кадр видео (а не рисованную заглушку). */
  private lastTexVideo = false;
  private hitSrc: HitSource = { fire: null, shot: null, blast: null };
  private hitSrcArmed = false;
  private pass = (ctx: StoryPassContext): void => this.render(ctx);

  constructor(host: StoryHost) {
    this.host = host;
    this.rend = new StoryRenderer(host.gl);
    this.hud = new StoryHud();
    this.hud.onAgain = () => {
      this.hud.hideEnd();
      host.onAgain();
    };
    this.hud.onMenu = () => {
      this.hud.hideEnd();
      host.onMenu();
    };
    // ?wdebug=1 — ручки для автотестов/кадров (как window.__weapons).
    if (new URLSearchParams(location.search).get("wdebug") === "1") {
      (window as unknown as Record<string, unknown>).__story = {
        client: this,
        stage: () => this.stage,
        mobs: () => [...this.views.values()].map((v) => ({
          id: v.id, kind: v.kind, state: v.state, hp: v.hp,
          x: +v.x.toFixed(2), y: +v.y.toFixed(2), z: +v.z.toFixed(2),
        })),
      };
    }
  }

  /** Комната сюжетная (по Hello или по первому снапшоту с блоком). */
  isActive(): boolean {
    return this.active;
  }

  onHello(h: HelloMsg): void {
    if (h.storyMode === 1) this.activate();
    else if (this.active) this.deactivate();
  }

  onSnapshot(snap: Snapshot): void {
    const st = snap.story;
    if (!st || st.stage === StoryStage.None) {
      if (this.active && snap.story) this.deactivate();
      return;
    }
    if (!this.active) this.activate();
    if (st.stage !== this.stage) {
      if (st.stage > 0 && st.stage !== StoryStage.Victory) track("story_stage", { stage: st.stage });
      const fb = STAGE_TEXT[st.stage];
      if (fb && (this.stage === 0 || !this.stageTitle)) this.stageTitle = fb.title;
      this.stage = st.stage;
    }
    this.flags = st.flags;
    this.timeSec = st.timeSec;
    this.kills = st.kills;
    this.ghosts = st.ghosts;
    this.wx = st.wx;
    this.wy = st.wy;
    this.wz = st.wz;

    const now = performance.now();
    const seen = new Set<number>();
    for (const m of st.mobs) {
      seen.add(m.id);
      const ageMs = m.age * TICK_MS;
      let v = this.views.get(m.id);
      if (!v) {
        v = {
          id: m.id, kind: m.kind, state: m.state, variant: m.variant,
          x: m.x, y: m.y, z: m.z, yaw: m.yaw,
          tx: m.x, ty: m.y, tz: m.z, tyaw: m.yaw,
          hp: m.hp, stateStart: now - ageMs, oneShot: null,
          mirror: false, flashUntil: 0, flashAmp: 1, seed: (m.id * 7.31) % 13.7, spd: 0, walkRate: 0,
        };
        this.views.set(m.id, v);
      } else {
        if (v.state !== m.state) {
          // Dying → Dead — тот же клип die: плеер не отпускаем. Раньше его
          // отпускали и тут же брали снова с фазой «0 с от входа в Dead» —
          // клип перематывался в начало, и упавший зомби вставал, прежде
          // чем растаять.
          if (slotFor(v.kind, v.state, v.variant) !== slotFor(m.kind, m.state, m.variant)) this.releaseOneShot(v);
          v.state = m.state;
          v.stateStart = now - ageMs;
        }
        if (Math.hypot(m.x - v.tx, m.y - v.ty) > 4 || Math.abs(m.z - v.tz) > 2.5) {
          v.x = m.x; v.y = m.y; v.z = m.z; // возрождение/телепорт — без скольжения
        }
        v.tx = m.x; v.ty = m.y; v.tz = m.z; v.tyaw = m.yaw;
      }
      v.hp = m.hp;
      v.variant = m.variant;
      if (m.kind === StoryKind.Hag) this.bossHp = m.hp;
    }
    for (const [id, v] of this.views) {
      if (!seen.has(id)) {
        this.releaseOneShot(v);
        this.views.delete(id);
      }
    }
    if (st.stage === StoryStage.Victory && !this.endShown && this.victoryAt === 0) {
      // Вошли уже после победы (события не видели) — показываем итог.
      this.scheduleEnd(0);
    }
  }

  /** Событие сюжета; true — обработано здесь. Прочие события только
   *  запоминаются как источник попадания (кровь) и идут дальше в main. */
  onEvent(ev: GameEvent): boolean {
    if (ev.type !== EvType.Story) {
      if (this.active) this.noteHitSource(ev);
      return false;
    }
    if (!this.active) this.activate();
    const v = this.views.get(ev.mobId);
    switch (ev.code) {
      case StoryEv.Stage: {
        const [title, hint] = ev.text.split("\n");
        const fb = STAGE_TEXT[ev.a];
        this.stage = ev.a;
        this.stageTitle = title || fb?.title || "";
        if (ev.a !== StoryStage.Victory) {
          this.hud.showBanner(storyText(this.stageTitle, fb?.title), storyText(hint ?? "", fb?.hint));
          this.sound("stage", null);
        }
        break;
      }
      case StoryEv.Whisper:
        this.hud.showWhisper(storyText(ev.text));
        this.sound("whisper", null);
        if (v) {
          v.flashUntil = performance.now() + 120;
          v.flashAmp = 1;
        }
        break;
      case StoryEv.MobDie:
        this.sound("mob_die", ev);
        // Смертельный удар MobHurt не шлёт: кровь здесь, гуще. Точка — луча,
        // если он был (в событии — ступни), иначе грудь.
        if (v) {
          const sh = this.hitSrc.shot;
          if (sh && Math.hypot(sh.hx - ev.x, sh.hy - ev.y) < 1.2 && sh.hz > ev.z - 0.3 && sh.hz < ev.z + 2.4) {
            this.bleed(v, sh.hx, sh.hy, sh.hz, ev.z, true);
          } else {
            this.bleed(v, ev.x, ev.y, ev.z + 1.1, ev.z, true);
          }
        }
        break;
      case StoryEv.Dissolve:
        this.sound("ghost_vanish", ev);
        break;
      case StoryEv.Scare:
        this.hud.flashScare();
        this.sound("scare", null);
        break;
      case StoryEv.Hit:
        this.sound(v && v.kind === StoryKind.Hag ? "hag_hit" : "zombie_hit", ev);
        break;
      case StoryEv.Book:
        if (ev.a === 1) this.sound("book", null);
        break;
      case StoryEv.Victory:
        this.timeSec = ev.a;
        this.kills = ev.b;
        this.hud.showBanner(t("Книга ваша"), "");
        this.scheduleEnd(END_DELAY_MS);
        break;
      case StoryEv.Restart: {
        const fb = STAGE_TEXT[ev.a];
        this.hud.showBanner(t("Этап сначала"), storyText(this.stageTitle, fb?.title), 2600);
        break;
      }
      case StoryEv.MobHurt:
        if (v) {
          // Красная вспышка — едва заметная: попадание показывает кровь.
          v.flashUntil = performance.now() + FLASH_MS;
          v.flashAmp = HURT_FLASH_AMP;
          v.hp = ev.a;
          if (v.kind === StoryKind.Hag) this.bossHp = ev.a;
          this.bleed(v, ev.x, ev.y, ev.z, v.tz, false);
        }
        break;
    }
    return true;
  }

  /** Кадр: сглаживание позиций, HUD цели, плееры клипов. */
  frame(dtSec: number): void {
    if (!this.active) return;
    const k = 1 - Math.exp(-dtSec / LERP_TAU);
    for (const v of this.views.values()) {
      const ox = v.x, oy = v.y;
      v.x += (v.tx - v.x) * k;
      v.y += (v.ty - v.y) * k;
      if (dtSec > 0) {
        const inst = Math.hypot(v.x - ox, v.y - oy) / dtSec;
        v.spd += (inst - v.spd) * (1 - Math.exp(-dtSec / SPEED_TAU));
      }
      v.z += (v.tz - v.z) * k;
      let dy = v.tyaw - v.yaw;
      while (dy > Math.PI) dy -= Math.PI * 2;
      while (dy < -Math.PI) dy += Math.PI * 2;
      v.yaw += dy * k;
    }
    const p = this.host.player();
    this.hud.setGoal(this.stage, TOTAL_STAGES, storyText(this.stageTitle, STAGE_TEXT[this.stage]?.title));
    this.hud.setStats(this.timeSec, this.kills, this.ghosts);
    this.hud.setBoss((this.flags & StoryFlag.BossLive) !== 0, this.bossHp);
    if (p && (this.flags & StoryFlag.Waypoint) !== 0 && this.stage !== StoryStage.Victory) {
      const dx = this.wx - p.x;
      const dy = this.wy - p.y;
      let rel = Math.atan2(dy, dx) - p.yaw;
      while (rel > Math.PI) rel -= Math.PI * 2;
      while (rel < -Math.PI) rel += Math.PI * 2;
      this.hud.setNav(true, rel, Math.hypot(dx, dy), this.wz - p.z);
    } else {
      this.hud.setNav(false, 0, 0, 0);
    }
    this.bank?.tick(performance.now());
  }

  /** Свет: книга (фиолетовый) и призраки (холодный). */
  appendLights(out: PointLight[]): void {
    if (!this.active) return;
    let ghosts = 0;
    const t = performance.now() / 1000;
    for (const v of this.views.values()) {
      if (v.kind === StoryKind.Book && v.state === StoryState.Idle) {
        out.push({ x: v.x, y: v.y, z: v.z + 0.7, range: 4.5, color: [0.55 + 0.1 * Math.sin(t * 3), 0.22, 1.0], noBulb: true });
      } else if ((v.kind === StoryKind.GhostGirl || v.kind === StoryKind.GhostWoman) &&
        v.state === StoryState.Move && ghosts < 2) {
        ghosts++;
        out.push({ x: v.x, y: v.y, z: v.z + 1.2, range: 3.2, color: [0.25, 0.42, 0.6], noBulb: true });
      }
    }
  }

  /** Новая сессия / выход из режима. */
  reset(): void {
    this.deactivate();
  }

  // ── внутреннее ──────────────────────────────────────────────────────

  private activate(): void {
    if (this.active) return;
    this.active = true;
    this.bank = new VideoBank(this.host.gl);
    this.host.renderer.storyPass = this.pass;
    this.hud.setVisible(true);
    // Петли грузим сразу: пока игрок идёт по саду, клипы дома уже в кэше,
    // а отсутствующие файлы успевают «упасть» на заглушку.
    const now = performance.now();
    for (const [name, def] of Object.entries(VIDEO_SLOTS)) {
      this.bank.shared(name, now - 10000);
      // Последний кадр смерти паркуем заранее: к первому трупу он уже готов.
      if (def.holdLast) this.bank.endFrame(name, now - 10000);
    }
  }

  private deactivate(): void {
    if (!this.active && !this.bank) {
      this.hud.reset();
      return;
    }
    this.active = false;
    if (this.host.renderer.storyPass === this.pass) this.host.renderer.storyPass = null;
    for (const v of this.views.values()) this.releaseOneShot(v);
    this.views.clear();
    this.bank?.dispose();
    this.bank = null;
    this.hud.reset();
    window.clearTimeout(this.endTimer);
    this.stage = 0;
    this.flags = 0;
    this.stageTitle = "";
    this.victoryAt = 0;
    this.endShown = false;
    this.bossHp = 100;
  }

  private scheduleEnd(delay: number): void {
    if (this.victoryAt === 0) track("story_victory", { time_sec: Math.round(this.timeSec), kills: this.kills });
    this.victoryAt = performance.now();
    window.clearTimeout(this.endTimer);
    this.endTimer = window.setTimeout(() => {
      if (!this.active || this.endShown) return;
      this.endShown = true;
      this.hud.showEnd({
        timeSec: this.timeSec, kills: this.kills, ghosts: this.ghosts, deaths: this.host.myDeaths(),
      });
      try { document.exitPointerLock(); } catch { /* */ }
    }, delay);
  }

  private releaseOneShot(v: MobView): void {
    if (v.oneShot) {
      this.bank?.releaseOneShot(v.oneShot);
      v.oneShot = null;
    }
  }

  private sound(name: string, at: { x: number; y: number; z: number } | null): void {
    const def = STORY_SOUNDS[name];
    if (!def) return;
    let vol = def.vol * this.host.soundVolume();
    if (at) {
      const p = this.host.player();
      if (p) {
        const d = Math.hypot(at.x - p.x, at.y - p.y, at.z - p.z);
        if (d > HEAR_MAX) return;
        if (d > HEAR_FULL) vol *= 1 - (d - HEAR_FULL) / (HEAR_MAX - HEAR_FULL);
      }
    }
    if (vol <= 0.01) return;
    const known = this.soundUrl.get(name);
    if (known) {
      playOneShot(known, vol, def.rate);
      return;
    }
    // Свой файл, пока его нет, — заглушка из уже существующих звуков.
    void loadSfx(def.file).then((buf) => {
      const url = buf ? def.file : def.stub;
      this.soundUrl.set(name, url);
      playOneShot(url, vol, def.rate);
    }).catch(() => {
      this.soundUrl.set(name, def.stub);
    });
  }

  /** Запомнить источник попадания из не-сюжетного события этой пачки. */
  private noteHitSource(ev: GameEvent): void {
    const h = this.hitSrc;
    switch (ev.type) {
      case EvType.WeaponFire:
        if (ev.shooterKind !== ShooterKind.Player) return;
        h.fire = { weapon: ev.weapon, dx: ev.dx, dy: ev.dy, dz: ev.dz };
        h.shot = null;
        break;
      case EvType.Shot:
        h.shot = ev.surface === ShotSurf.Npc
          ? { ox: ev.ox, oy: ev.oy, oz: ev.oz, hx: ev.hx, hy: ev.hy, hz: ev.hz }
          : null;
        break;
      case EvType.Explosion:
        h.blast = { x: ev.x, y: ev.y, z: ev.z };
        break;
      default:
        return;
    }
    // Пачка событий разбирается синхронно; микрозадача — сразу после неё:
    // горение в следующей пачке не примет чужой выстрел за свой.
    if (!this.hitSrcArmed) {
      this.hitSrcArmed = true;
      queueMicrotask(() => {
        h.fire = null;
        h.shot = null;
        h.blast = null;
        this.hitSrcArmed = false;
      });
    }
  }

  /** Брызги крови из точки попадания по зомби/старухе (призраки не кровят).
   *  floorZ — пол под существом (капли ложатся на этаж, а не на z = 0). */
  private bleed(v: MobView, hx: number, hy: number, hz: number, floorZ: number, kill: boolean): void {
    if (v.kind !== StoryKind.Zombie && v.kind !== StoryKind.Hag) return;
    const h = this.hitSrc;
    let dx = 0, dy = 0, dz = 0;
    const sh = h.shot;
    if (sh && Math.hypot(sh.hx - hx, sh.hy - hy, sh.hz - hz) < 0.6) {
      dx = sh.hx - sh.ox; dy = sh.hy - sh.oy; dz = sh.hz - sh.oz; // пуля/дробина/лазер
    } else if (h.fire && h.fire.weapon !== WeaponId.Flamethrower) {
      dx = h.fire.dx; dy = h.fire.dy; dz = h.fire.dz; // кулак/пила: по взгляду
    } else if (h.blast) {
      dx = hx - h.blast.x; dy = hy - h.blast.y; dz = hz - h.blast.z + 0.4; // от взрыва
    } else {
      return; // горение / огнемёт: огонь не режет
    }
    const dl = Math.hypot(dx, dy, dz);
    if (dl < 1e-4) return;
    dx /= dl; dy /= dl; dz /= dl;
    const r = this.host.renderer;
    const n = kill ? BLOOD_DROPS_KILL : BLOOD_DROPS_HIT;
    for (let i = 0; i < n; i++) {
      // Две трети — выходной веер по ходу пули (виден по краям силуэта),
      // треть — брызги назад, к стрелку: их видно на фоне самого существа.
      const back = i % 3 === 0;
      const sgn = back ? -1 : 1;
      const cone = back ? 0.85 : 0.5;
      let ex = sgn * dx + (Math.random() * 2 - 1) * cone;
      let ey = sgn * dy + (Math.random() * 2 - 1) * cone;
      let ez = sgn * dz * 0.5 + (Math.random() * 2 - 1) * cone * 0.7;
      const el = Math.hypot(ex, ey, ez) || 1;
      ex /= el; ey /= el; ez /= el;
      const sp = back ? 1.2 + Math.random() * 2.3 : 2.4 + Math.random() * (kill ? 5 : 4);
      r.spawnChunkDebris(
        hx + (Math.random() - 0.5) * 0.12,
        hy + (Math.random() - 0.5) * 0.12,
        hz + (Math.random() - 0.5) * 0.14,
        ex * sp, ey * sp, ez * sp + 0.8 + Math.random() * 1.6,
        BLOOD_SHADES[i % BLOOD_SHADES.length]!,
        BODY_FLESH_DEBRIS_CUBE_SIZE * (0.38 + Math.random() * 0.5),
        { floorZ, splat: true }, // капля: прилипает к полу без пыли
      );
    }
    // Мелкая морось тех же «песчинок», что у осколков (точечный стиль).
    r.spawnDustBurst(hx, hy, hz, dx * 1.6, dy * 1.6, 0.4, BLOOD_DEBRIS_COLOR, floorZ, kill ? 18 : 8);
  }

  /** Текстура кадра существа (null — пока нечего показать). */
  private textureFor(v: MobView, slot: string, now: number): WebGLTexture | null {
    const bank = this.bank;
    if (!bank) return null;
    const def = VIDEO_SLOTS[slot];
    const shared = bank.shared(slot, now);
    let src: VideoSource | null = shared;
    // Ходьба: темп шагов — под скорость плоскости (ноги не скользят по полу).
    const meters = shared && def?.loop ? walkMeters(slot, shared.url) : undefined;
    if (shared && meters && shared.ready) {
      const d = shared.video.duration;
      const loopSec = Number.isFinite(d) && d > 0 ? d : def!.seconds;
      v.walkRate = pickWalkRate(v.walkRate, (v.spd * loopSec) / meters);
      src = bank.sharedAt(slot, now, v.walkRate);
    }
    if (def?.holdLast && v.state === StoryState.Dead) {
      // Тело лежит: свой клип смерти доигрываем до конца, дальше — общий
      // запаркованный последний кадр (плеер из пула свободен для следующих).
      // Общий плеер слота стоит на ПЕРВОМ кадре (стоящий зомби) — его в Dead
      // не показываем никогда: лучше кадр-другой пустоты, чем «встал».
      const gl = this.host.gl;
      const os = v.oneShot;
      const end = bank.endFrame(slot, now);
      this.lastRealClip = !!end && end.url.includes(`/${def.name}.`);
      this.lastTexVideo = true;
      // Доигрываем, только пока плеер реально играет: застрявший на паузе
      // (автозапуск запрещён) мог остаться на первом кадре.
      const vid = os?.src.video;
      if (os && vid && vid.readyState >= 2 && !vid.paused && !os.src.done()) return os.src.upload(gl, now);
      const endTex = end ? end.upload(gl, now) : null;
      if (endTex) {
        this.releaseOneShot(v);
        return endTex;
      }
      return os && os.src.done() ? os.src.upload(gl, now) : null;
    }
    if (def && !def.loop) {
      // Однократный клип — свой плеер с фазой от входа в состояние.
      if (!v.oneShot) {
        v.oneShot = bank.acquireOneShot(slot, now);
        if (v.oneShot) {
          const vid = v.oneShot.src.video;
          const start = (): void => {
            // Фаза — на момент запуска, а не взятия плеера: пока ролик
            // грузился, состояние уже шло (иначе клип отстаёт на время загрузки).
            const off = Math.max(0, (performance.now() - v.stateStart) / 1000);
            // Ролик Kling длиннее действия на сервере (удар 1,4 с, смерть 1,7 с):
            // ускоряем его так, чтобы он уложился в состояние. Заглушку не трогаем.
            const dur = vid.duration || def.seconds;
            const real = vid.src.includes(`/${def.name}.`);
            const rate = real && dur > def.seconds ? Math.min(3, dur / def.seconds) : 1;
            vid.playbackRate = rate;
            try { vid.currentTime = Math.min(off * rate, Math.max(0, dur - 0.05)); } catch { /* */ }
            void vid.play().catch(() => { /* */ });
          };
          if (vid.readyState >= 1) start();
          else vid.addEventListener("loadedmetadata", start, { once: true });
        }
      }
      if (v.oneShot && v.oneShot.src.video.readyState >= 2) {
        src = v.oneShot.src;
      } else if (def.holdLast && (now - v.stateStart) / 1000 > def.seconds * 0.5) {
        // Своего плеера смерти нет (пул занят — гранатой положили толпу):
        // вторую половину клипа показываем лежащее тело, а не стоящее.
        const end = bank.endFrame(slot, now);
        const endTex = end ? end.upload(this.host.gl, now) : null;
        if (endTex) {
          this.lastRealClip = end!.url.includes(`/${def.name}.`);
          this.lastTexVideo = true;
          return endTex;
        }
      }
    }
    this.lastRealClip = !!src && !!def && src.url.includes(`/${def.name}.`);
    this.lastTexVideo = !!src;
    if (src) {
      if (def?.loop && !(v.kind === StoryKind.Painting && v.state !== StoryState.Awake)) src.wantPlay = true;
      if (src === shared) shared.lastUsed = now;
      const tex = src.upload(this.host.gl, now);
      if (tex) return tex;
      if (src !== shared && shared) return shared.upload(this.host.gl, now);
      return null;
    }
    if (bank.exhausted(slot)) {
      return v.kind === StoryKind.Book
        ? bank.proceduralTex("book", drawBook)
        : v.kind === StoryKind.Painting ? null : bank.proceduralTex("silhouette", drawSilhouette);
    }
    return null;
  }

  private render(ctx: StoryPassContext): void {
    if (!this.active || this.views.size === 0) return;
    const now = performance.now();
    const ex = ctx.eye[0]!, ey = ctx.eye[1]!, ez = ctx.eye[2]!;
    const quads = this.quads;
    quads.length = 0;
    for (const v of this.views.values()) {
      const look = KIND_LOOK[v.kind];
      const slot = slotFor(v.kind, v.state, v.variant);
      if (!look || !slot) continue;
      const tex = this.textureFor(v, slot, now);
      const realClip = this.lastRealClip;
      const packed = VIDEO_PACKED_ALPHA && this.lastTexVideo;
      if (!tex && v.kind !== StoryKind.Painting) continue;

      const sec = (now - v.stateStart) / 1000;
      let dissolve = 0;
      let sink = 0;
      let alpha = look.alpha;
      let tint = look.tint;
      let emissive = look.emissive;
      let corpse = 0;
      if (v.state === StoryState.Dissolve) {
        dissolve = Math.min(1, sec / DISSOLVE_SEC);
      } else if (v.state === StoryState.Dead) {
        // Тело сначала просто лежит (последний кадр смерти), потом плавно
        // уходит в пол и тает по шуму — к концу CORPSE_SEC его нет.
        const k = Math.min(1, Math.max(0, (sec - CORPSE_HOLD_SEC) / (CORPSE_SEC - CORPSE_HOLD_SEC)));
        corpse = k;
        // Шум fbm по лежащему телу — примерно 0,2…0,8: порог ведём ровно по
        // этому диапазону, и тело тает равномерно все 1,8 с (при 0…1 оно
        // съедалось за последние полсекунды). k = 1 — порог выше любого шума.
        dissolve = k > 0 ? 0.22 + 0.6 * k : 0;
        sink = CORPSE_SINK * k * k * (3 - 2 * k);
      }
      if (v.kind === StoryKind.GhostGirl || v.kind === StoryKind.GhostWoman) {
        alpha *= 0.88 + 0.12 * Math.sin(now / 170 + v.seed);
      }
      if (v.kind === StoryKind.Painting) {
        const awake = v.state === StoryState.Awake;
        tint = awake ? [1.05, 1.0, 0.95] : [0.5, 0.48, 0.46];
        emissive = awake ? 0.1 + 0.06 * Math.sin(now / 90 + v.seed) : 0;
      }
      const flash = v.flashUntil > now ? Math.min(1, (v.flashUntil - now) / FLASH_MS) * v.flashAmp : 0;

      let ox: number, oy: number, oz: number, rx: number, ry: number, nx: number, ny: number;
      const size = look.size;
      if (v.kind === StoryKind.Painting) {
        const fx = Math.cos(v.yaw), fy = Math.sin(v.yaw);
        rx = -fy; ry = fx; nx = fx; ny = fy;
        ox = v.x; oy = v.y; oz = v.z - size / 2;
      } else {
        let tx = ex - v.x, ty = ey - v.y;
        const tl = Math.hypot(tx, ty);
        if (tl < 1e-3) { tx = 1; ty = 0; } else { tx /= tl; ty /= tl; }
        rx = -ty; ry = tx; nx = tx; ny = ty;
        ox = v.x; oy = v.y;
        let hover = look.hover;
        if (v.kind === StoryKind.Book) hover += 0.07 * Math.sin(now / 420);
        oz = v.z + hover - look.footPad * size - sink;
        if (look.mirrorByMotion) {
          const m = Math.cos(v.yaw) * rx + Math.sin(v.yaw) * ry;
          if (m < -0.25) v.mirror = true;
          else if (m > 0.25) v.mirror = false;
        }
        if (look.shadow > 0 && corpse < 1) {
          // Пятно тени на полу (лежит горизонтально): «вверх» квада — к камере.
          // У тающего тела тень гаснет вместе с ним, а не пропадает разом.
          quads.push(this.quad(null, 4, v.x - tx * 0.32, v.y - ty * 0.32, v.z + 0.03,
            rx, ry, 0, tx, ty, 0, v.kind === StoryKind.Book ? 0.45 : 0.95, 0.64, false,
            [0, 0, 0], look.shadow * (1 - corpse), 0, [0, 0, 0], 0, 0, 0, 0, 1, 0,
            (v.x - ex) ** 2 + (v.y - ey) ** 2 + (v.z - ez) ** 2 + 0.01));
          // Тени тот же сдвиг глубины: на лестнице кромка ступени резала мягкое
          // пятно в чёрный прямоугольник — «яма» под ногами.
          quads[quads.length - 1]!.pull = BODY_DEPTH_PULL;
        }
      }
      const cz = oz + size * 0.5;
      // Картина из настоящего ролика уже в раме — рисуем кадр целиком (режим 5).
      const mode = v.kind === StoryKind.Painting && realClip ? 5 : look.mode;
      quads.push(this.quad(tex, mode, ox, oy, oz, rx, ry, 0, 0, 0, 1, size, size, v.mirror,
        tint, alpha, dissolve, look.edge, flash, emissive, nx, ny, 0.25, v.seed,
        (v.x - ex) ** 2 + (v.y - ey) ** 2 + (cz - ez) ** 2));
      const q = quads[quads.length - 1]!;
      q.packed = packed;
      if (v.kind !== StoryKind.Painting && v.kind !== StoryKind.Book) q.pull = BODY_DEPTH_PULL;
    }
    this.rend.draw(ctx, quads);
  }

  private quad(
    tex: WebGLTexture | null, mode: number,
    ox: number, oy: number, oz: number,
    rx: number, ry: number, rz: number,
    ux: number, uy: number, uz: number,
    w: number, h: number, mirror: boolean,
    tint: [number, number, number], alpha: number, dissolve: number, edge: [number, number, number],
    flash: number, emissive: number, nx: number, ny: number, nz: number, seed: number, d2: number,
  ): StoryQuad {
    const nl = Math.hypot(nx, ny, nz) || 1;
    return {
      tex, mode, ox, oy, oz, rx, ry, rz, ux, uy, uz, w, h, mirror, tint, alpha, dissolve, edge,
      flash, emissive, nx: nx / nl, ny: ny / nl, nz: nz / nl, seed, d2,
    };
  }
}

/** Метры за петлю ходьбы для ролика, который реально играет (слот без своего
 *  файла — например, «стоит на месте» — берёт ходьбу из fallback). */
function walkMeters(slot: string, url: string): number | undefined {
  let s: string | undefined = slot;
  for (let guard = 0; s && guard < 4; guard++) {
    const d: (typeof VIDEO_SLOTS)[string] | undefined = VIDEO_SLOTS[s];
    if (!d) break;
    if (url.includes(`/${d.name}.`)) return d.metersPerLoop;
    s = d.fallback;
  }
  return undefined;
}

/** Ближайшая скорость ролика из WALK_RATES; текущую держим, пока нужная не
 *  ушла от неё больше чем на 12 % (соседние ступени — через 15–50 %): на
 *  разгоне плееры не перебираются, а на ходу ноги не отстают. */
function pickWalkRate(cur: number, want: number): number {
  if (cur > 0 && want > cur / 1.12 && want < cur * 1.12) return cur;
  const w = Math.max(want, 0.05);
  let best = WALK_RATES[0]!;
  for (const r of WALK_RATES) {
    if (Math.abs(Math.log(r / w)) < Math.abs(Math.log(best / w))) best = r;
  }
  return best;
}

/** Рисованная заглушка существа: тёмный силуэт (если не загрузилось ни одно видео). */
function drawSilhouette(c: CanvasRenderingContext2D, w: number, h: number): void {
  c.clearRect(0, 0, w, h);
  c.fillStyle = "rgba(40, 46, 36, 0.95)";
  c.beginPath();
  c.ellipse(w * 0.5, h * 0.17, w * 0.08, h * 0.08, 0, 0, Math.PI * 2); // голова
  c.fill();
  c.beginPath();
  c.moveTo(w * 0.36, h * 0.27);
  c.lineTo(w * 0.64, h * 0.27);
  c.lineTo(w * 0.78, h * 0.5); // вытянутая рука
  c.lineTo(w * 0.62, h * 0.45);
  c.lineTo(w * 0.6, h * 0.62);
  c.lineTo(w * 0.62, h * 0.96);
  c.lineTo(w * 0.53, h * 0.96);
  c.lineTo(w * 0.5, h * 0.66);
  c.lineTo(w * 0.46, h * 0.96);
  c.lineTo(w * 0.37, h * 0.96);
  c.lineTo(w * 0.4, h * 0.62);
  c.lineTo(w * 0.38, h * 0.45);
  c.lineTo(w * 0.24, h * 0.52);
  c.closePath();
  c.fill();
  c.fillStyle = "rgba(220, 40, 30, 0.9)";
  c.fillRect(w * 0.465, h * 0.155, w * 0.02, h * 0.012);
  c.fillRect(w * 0.515, h * 0.155, w * 0.02, h * 0.012);
}

/** Рисованная книга заклинаний (своего клипа «book» может и не быть). */
function drawBook(c: CanvasRenderingContext2D, w: number, h: number): void {
  c.clearRect(0, 0, w, h);
  const g = c.createRadialGradient(w / 2, h / 2, 10, w / 2, h / 2, w * 0.5);
  g.addColorStop(0, "rgba(170, 90, 255, 0.55)");
  g.addColorStop(1, "rgba(80, 20, 140, 0)");
  c.fillStyle = g;
  c.fillRect(0, 0, w, h);
  // Обложка.
  c.fillStyle = "#3b1418";
  c.strokeStyle = "#c9a24a";
  c.lineWidth = 5;
  c.beginPath();
  c.roundRect(w * 0.22, h * 0.18, w * 0.56, h * 0.66, 10);
  c.fill();
  c.stroke();
  // Корешок.
  c.fillStyle = "#2a0d10";
  c.fillRect(w * 0.22, h * 0.18, w * 0.08, h * 0.66);
  // Знак: круг и пентаграмма.
  c.strokeStyle = "#e9c46a";
  c.lineWidth = 3;
  const cx = w * 0.54, cy = h * 0.5, r = w * 0.15;
  c.beginPath();
  c.arc(cx, cy, r, 0, Math.PI * 2);
  c.stroke();
  c.beginPath();
  for (let i = 0; i <= 5; i++) {
    const a = -Math.PI / 2 + (i * 4 * Math.PI) / 5;
    const px = cx + Math.cos(a) * r * 0.92, py = cy + Math.sin(a) * r * 0.92;
    if (i === 0) c.moveTo(px, py);
    else c.lineTo(px, py);
  }
  c.stroke();
}
